import { useEffect, useState, MouseEvent } from "react";
import { logout, getProfile, KeycloakProfile } from "../services/keycloak";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DomainIcon from "@mui/icons-material/Domain";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Container from "./Container";
import { COMPACT_QUERY } from "../utils/hooks/useCompact";

const compact = `@media ${COMPACT_QUERY}`;

/** Top-bar height — shared so the Sidebar can sit flush beneath it full-height. */
export const HEADER_HEIGHT = 65;
export const HEADER_HEIGHT_COMPACT = 52;

export default function Header() {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<KeycloakProfile>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const onGetProfile = async () => {
      const profile = await getProfile();
      setUserProfile(profile);
    };

    onGetProfile();
  }, []);

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.primary.main,
        width: "100%",
        flexShrink: 0,
        height: `${HEADER_HEIGHT}px`,
        [compact]: { height: `${HEADER_HEIGHT_COMPACT}px` },
      }}
    >
      <Container>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
            onClick={() => navigate("/")}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "48px",
                width: "48px",
                borderRadius: "100%",
                background: "#FFFFFF29",
                [compact]: { height: "38px", width: "38px" },
              }}
            >
              <Box
                component="img"
                alt="Gold Bread Logo"
                src="/icons/logo.png"
                sx={{
                  height: "42px",
                  width: "42px",
                  [compact]: { height: "32px", width: "32px" },
                }}
              />
            </Box>
            <Typography variant="body1" fontWeight={600} color="#fff">
              GB Data Platform
            </Typography>
          </Box>
          {userProfile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  color: "#fff",
                  alignItems: "center",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  backgroundColor: "rgb(255,255,255, 0.4)",
                }}
              >
                <DomainIcon />
                <Typography fontWeight={500} sx={{ ml: 1 }}>
                  {userProfile.attributes.branchCode.toString()}{" "}
                  {userProfile.attributes.branchName.toString()}
                </Typography>
              </Box>
              <Box sx={{ width: "2px", height: "26px", bgcolor: "#fff" }} />
              <Box>
                <Button
                  onClick={handleMenuClick}
                  sx={{ color: "#fff", textTransform: "none" }}
                  endIcon={<KeyboardArrowDownIcon />}
                >
                  {userProfile.firstName} {userProfile.lastName}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: `${anchorEl?.clientWidth}px`,
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      justifyContent: "center",
                    }}
                  >
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
